import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import PageTransition from "../components/PageTransition";
import axios from "axios";
import toast from "react-hot-toast";
import { BASE_ENDPOINT } from "../helpers/Config";
import { useNavigate, useParams } from "react-router-dom";
import {
    FaArrowUp,
    FaArrowDown,
    FaListUl,
    FaChartLine,
    FaSave,
    FaEdit,
    FaCheckCircle,
    FaTimesCircle
} from "react-icons/fa";

import {
    FaArrowTrendUp,
    FaArrowTrendDown
} from "react-icons/fa6";
import TradingViewAdvancedChart, {
    entityToTradingViewSymbol,
    periodToTradingViewInterval,
} from "../components/TradingViewAdvancedChart";

function safeText(v) {
    return v === null || v === undefined || v === "" ? "—" : String(v);
}

function nfmt(v, digits = 0) {
    const num = Number(v);
    if (!Number.isFinite(num)) return safeText(v);
    return new Intl.NumberFormat("en-IN", { maximumFractionDigits: digits }).format(num);
}

function moneyFmt(v, digits = 2) {
    const num = Number(v);
    if (!Number.isFinite(num)) return safeText(v);
    return new Intl.NumberFormat("en-IN", { maximumFractionDigits: digits }).format(num);
}

function sideNorm(side) {
    return String(side || "").toLowerCase();
}

function getCountdownTone(countdown) {
    const parts = countdown.split(":").map(Number);
    let totalSeconds = 0;

    if (parts.length === 3) {
        totalSeconds = parts[0] * 3600 + parts[1] * 60 + parts[2];
    } else {
        totalSeconds = parts[0] * 60 + parts[1];
    }

    return totalSeconds <= 10 ? "text-danger" : "text-primary";
}

function CountdownTimer({ countdown, className = "" }) {
    return (
        <span className={`fw-bold mono ${getCountdownTone(countdown)} ${className}`.trim()}>
            {countdown}
        </span>
    );
}

function periodMeta(p) {
    switch (p) {
        case "30s":
            return { title: "30s", sub: "30 second" };
        case "1m":
            return { title: "1m", sub: "1 minute" };
        case "3m":
            return { title: "3m", sub: "3 minute" };
        case "5m":
            return { title: "5m", sub: "5 minute" };
        case "10m":
            return { title: "10m", sub: "10 minute" };
        case "1h":
            return { title: "1h", sub: "1 hour" };
        default:
            return { title: p, sub: "" };
    }
}

export default function LiveTrades() {
    const navigate = useNavigate();
    const { entity_id, period } = useParams();

    // Refs for storing the timeout ID and preventing flicker
    const refreshTimeoutRef = useRef(null);
    const initialLoadDoneRef = useRef(false);
    const isMountedRef = useRef(true);
    const showManualTradeFormRef = useRef(false);

    const PERIODS = useMemo(() => ["30s", "1m", "3m", "5m", "10m", "1h"], []);

    const activePeriod = useMemo(() => {
        const p = String(period || "").trim();
        return PERIODS.includes(p) ? p : "30s";
    }, [period, PERIODS]);

    // Data
    const [entity, setEntity] = useState({ entity_name: "", image: "", period_id: "" });
    const [tradeList, setTradeList] = useState([]);
    const [manualTrade, setManualTrade] = useState({ has_manual_trade: false, side: "" });

    // Manual trade form state
    const [manualTradeSide, setManualTradeSide] = useState("");
    const [isSettingManualTrade, setIsSettingManualTrade] = useState(false);
    const [showManualTradeForm, setShowManualTradeForm] = useState(false);

    // Loading states
    const [isLoading, setIsLoading] = useState(true);
    const [countdown, setCountdown] = useState("00:00");
    const stats = useMemo(() => {
        let up = 0;
        let down = 0;
        let upCount = 0;
        let downCount = 0;
        for (const row of tradeList) {
            const s = sideNorm(row?.trade?.side);
            const amt = Number(row?.trade?.amount) || 0;
            if (s === "up") {
                up += amt;
                upCount += 1;
            } else if (s === "down") {
                down += amt;
                downCount += 1;
            }
        }

        return {
            total: up + down,
            up,
            down,
            totalCount: upCount + downCount,
            upCount,
            downCount,
        };
    }, [tradeList]);

    // Countdown timer based on active period
useEffect(() => {
    const getRemainingTime = () => {
        const now = new Date();

        const seconds = now.getSeconds();
        const minutes = now.getMinutes();
        const milliseconds = now.getMilliseconds();

        let remainingMs = 0;

        switch (activePeriod) {
            case "30s": {
                const nextBoundary = seconds < 30 ? 30 : 60;
                remainingMs =
                    ((nextBoundary - seconds) * 1000) - milliseconds;
                break;
            }

            case "1m": {
                remainingMs =
                    ((60 - seconds) * 1000) - milliseconds;
                break;
            }

            case "3m": {
                const nextMinute = Math.ceil((minutes + 1) / 3) * 3;
                const diffMinutes = nextMinute - minutes - 1;

                remainingMs =
                    (diffMinutes * 60 * 1000) +
                    ((60 - seconds) * 1000) -
                    milliseconds;
                break;
            }

            case "5m": {
                const nextMinute = Math.ceil((minutes + 1) / 5) * 5;
                const diffMinutes = nextMinute - minutes - 1;

                remainingMs =
                    (diffMinutes * 60 * 1000) +
                    ((60 - seconds) * 1000) -
                    milliseconds;
                break;
            }

            case "10m": {
                const nextMinute = Math.ceil((minutes + 1) / 10) * 10;
                const diffMinutes = nextMinute - minutes - 1;

                remainingMs =
                    (diffMinutes * 60 * 1000) +
                    ((60 - seconds) * 1000) -
                    milliseconds;
                break;
            }

            case "1h": {
                const remainingMinutes = 59 - minutes;

                remainingMs =
                    (remainingMinutes * 60 * 1000) +
                    ((60 - seconds) * 1000) -
                    milliseconds;
                break;
            }

            default: {
                remainingMs =
                    ((60 - seconds) * 1000) - milliseconds;
            }
        }

        const totalSeconds = Math.max(0, Math.floor(remainingMs / 1000));

        const hrs = Math.floor(totalSeconds / 3600);
        const mins = Math.floor((totalSeconds % 3600) / 60);
        const secs = totalSeconds % 60;

        if (hrs > 0) {
            return `${String(hrs).padStart(2, "0")}:${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
        }

        return `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
    };

    setCountdown(getRemainingTime());

    const interval = setInterval(() => {
        setCountdown(getRemainingTime());
    }, 250);

    return () => clearInterval(interval);
}, [activePeriod]);

    const fetchLiveTrades = useCallback(
        async (options = {}) => {
            const { isAutoRefresh = false } = options;

            if (!entity_id) return;

            if (!isAutoRefresh && !initialLoadDoneRef.current) {
                // Only set loading for initial load (first time)
                setIsLoading(true);
            }

            try {
                const res = await axios({
                    method: "post",
                    url: `${BASE_ENDPOINT}/admin/live-trades`,
                    headers: { "Content-Type": "application/json" },
                    data: {
                        token: localStorage.getItem("token") || "",
                        period: activePeriod,
                        entity_id: entity_id,
                    },
                    timeout: 15000,
                });

                const out = res?.data;

                if (out?.error) {
                    // Only show toast for non-auto-refresh errors
                    if (!isAutoRefresh) {
                        toast.error(typeof out?.error === "string" ? out.error : "Failed to load live trades");
                    }
                    return;
                }

                const payload = out?.data || {};
                const nextList = Array.isArray(payload?.trade_list) ? payload.trade_list : [];
                const nextEntity = payload?.entity || { entity_name: "", image: "", period_id: "" };
                const manualTradeData = out?.manual_trade || { has_manual_trade: false, side: "" };

                setTradeList(nextList);
                setEntity(nextEntity);
                setManualTrade(manualTradeData);

                // Only update manualTradeSide if form is NOT open
                // This prevents auto-refresh from resetting user's selection
                if (!showManualTradeFormRef.current) {
                    if (manualTradeData.has_manual_trade && manualTradeData.side) {
                        setManualTradeSide(manualTradeData.side);
                    } else {
                        setManualTradeSide("");
                    }
                }

                // Mark initial load as done after first successful fetch
                if (!initialLoadDoneRef.current && !isAutoRefresh) {
                    initialLoadDoneRef.current = true;
                }
            } catch (error) {
                if (!isAutoRefresh) {
                    if (error?.code === "ECONNABORTED") {
                        toast.error("Request timeout");
                    } else {
                        toast.error("Failed to load live trades");
                    }
                }
            } finally {
                // Only clear loading states if not auto-refresh
                if (!isAutoRefresh) {
                    setIsLoading(false);
                }
            }
        },
        [entity_id, activePeriod]
    );

    // Update ref when showManualTradeForm changes
    useEffect(() => {
        showManualTradeFormRef.current = showManualTradeForm;
    }, [showManualTradeForm]);

    // Function to set manual trade
    const setManualTradeResult = useCallback(async () => {
        if (!entity_id || !manualTradeSide) {
            toast.error("Please select a side");
            return;
        }

        setIsSettingManualTrade(true);
        try {
            const res = await axios({
                method: "post",
                url: `${BASE_ENDPOINT}/admin/set-manual-trade`,
                headers: { "Content-Type": "application/json" },
                data: {
                    token: localStorage.getItem("token") || "",
                    entity_id: entity_id,
                    period: activePeriod,
                    side: manualTradeSide
                },
                timeout: 15000,
            });

            const data = res?.data;

            if (data?.error) {
                toast.error(typeof data?.error === "string" ? data.error : "Failed to set manual trade");
                return;
            }

            toast.success("Manual trade result set successfully");

            // Update local state
            setManualTrade({
                has_manual_trade: true,
                side: manualTradeSide
            });

            // Update the form side to match what we just set
            setManualTradeSide(manualTradeSide);

            // Hide form after successful set
            setShowManualTradeForm(false);

            // Refresh data to get updated state
            fetchLiveTrades({ isManualRefresh: false });

        } catch (error) {
            if (error?.code === "ECONNABORTED") {
                toast.error("Request timeout");
            } else {
                toast.error("Failed to set manual trade");
            }
        } finally {
            setIsSettingManualTrade(false);
        }
    }, [entity_id, activePeriod, manualTradeSide, fetchLiveTrades]);

    // Setup auto-refresh every 3 seconds (no loading states)
    const setupAutoRefresh = useCallback(() => {
        // Clear any existing timeout
        if (refreshTimeoutRef.current) {
            clearTimeout(refreshTimeoutRef.current);
        }

        // Set new timeout for 3 seconds
        refreshTimeoutRef.current = setTimeout(() => {
            if (isMountedRef.current && entity_id) {
                fetchLiveTrades({ isAutoRefresh: true });
                setupAutoRefresh(); // Setup next refresh
            }
        }, 3000);
    }, [entity_id, fetchLiveTrades]);

    // Initial fetch when entity_id or period changes
    useEffect(() => {
        if (!entity_id) {
            setIsLoading(false);
            toast.error("entity_id missing in URL");
            return;
        }

        // Reset initial load flag for new entity/period
        initialLoadDoneRef.current = false;

        // Reset data for new entity/period
        setEntity({ entity_name: "", image: "", period_id: "" });
        setTradeList([]);
        setManualTrade({ has_manual_trade: false, side: "" });
        setManualTradeSide("");
        setShowManualTradeForm(false);

        // Fetch initial data (will show loading)
        fetchLiveTrades({});

        // Setup auto refresh (will not show loading)
        setupAutoRefresh();

        // Cleanup function to clear timeout on unmount or dependency change
        return () => {
            if (refreshTimeoutRef.current) {
                clearTimeout(refreshTimeoutRef.current);
                refreshTimeoutRef.current = null;
            }
        };
    }, [entity_id, activePeriod, fetchLiveTrades, setupAutoRefresh]);

    // Clear timeout when component unmounts
    useEffect(() => {
        isMountedRef.current = true;

        return () => {
            isMountedRef.current = false;
            if (refreshTimeoutRef.current) {
                clearTimeout(refreshTimeoutRef.current);
                refreshTimeoutRef.current = null;
            }
        };
    }, []);

    const onTab = (p) => {
        if (!entity_id) return;
        navigate(`/live-trades/${encodeURIComponent(entity_id)}/${encodeURIComponent(p)}`, { replace: true });
    };

    // Handle form opening - set the current manual trade side
    const handleOpenForm = () => {
        setShowManualTradeForm(true);
        // Set form side to current manual trade side if exists
        if (manualTrade.has_manual_trade && manualTrade.side) {
            setManualTradeSide(manualTrade.side);
        } else {
            setManualTradeSide("");
        }
    };

    // Handle form cancel - reset to current state
    const handleCancelForm = () => {
        setShowManualTradeForm(false);
        // Reset to current manual trade state
        if (manualTrade.has_manual_trade && manualTrade.side) {
            setManualTradeSide(manualTrade.side);
        } else {
            setManualTradeSide("");
        }
    };

    const coinName =
        safeText(entity?.entity_name) !== "—" && safeText(entity?.entity_name) !== ""
            ? safeText(entity?.entity_name)
            : safeText(entity_id);

    const tvSymbol = useMemo(
        () => entityToTradingViewSymbol(entity_id, entity?.entity_name),
        [entity_id, entity?.entity_name]
    );

    const tvInterval = useMemo(() => periodToTradingViewInterval(activePeriod), [activePeriod]);

    return (
        <PageTransition>
            <style>{`
        .btn-compact { padding: 4px 8px !important; line-height: 1.1 !important; }
        .mono { font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace; }
        .coin-img { width: 44px; height: 44px; border-radius: 12px; object-fit: cover; }
        .lt-table th, .lt-table td { padding: 6px 6px !important; vertical-align: middle; }
        .lt-table th:not(:last-child), .lt-table td:not(:last-child) { border-right: 1px solid rgba(0,0,0,0.06); }

        .tab-grid { display: grid; grid-template-columns: repeat(6, minmax(0, 1fr)); gap: 10px; }
        .period-tab {
          border: 1px solid rgba(0,0,0,0.12);
          background: #fff;
          border-radius: 14px;
          padding: 12px 12px;
          text-align: left;
          width: 100%;
          min-width: 0;
          transition: transform .10s ease, border-color .10s ease, background .10s ease;
        }
        .period-tab:hover { transform: translateY(-1px); border-color: rgba(13,110,253,0.35); background: rgba(13,110,253,0.05); }
        .period-tab.active { border-color: rgba(13,110,253,0.65); background: rgba(13,110,253,0.08); }
        .period-title { font-weight: 800; font-size: 1rem; line-height: 1.1; }
        .period-sub { margin-top: 4px; font-size: 0.78rem; color: rgba(0,0,0,0.55); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        @media (max-width: 575.98px) {
          .tab-grid { gap: 4px; }
          .period-tab { padding: 6px 4px; border-radius: 8px; text-align: center; }
          .period-title { font-size: 0.7rem; }
          .period-sub { display: none; }
        }

        .stat-cards-row { flex-wrap: nowrap; }
        .stat-cards-row > [class*="col-"] { min-width: 0; }
        .stat-card { border-radius: 14px; padding: 10px 12px; color: white; height: 100%; }
        .stat-total { background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); }
        .stat-up { background: linear-gradient(135deg, #0bab64 0%, #3bb78f 100%); }
        .stat-down { background: linear-gradient(135deg, #ff416c 0%, #ff4b2b 100%); }
        .stat-label { font-size: 0.78rem; color: rgba(255,255,255,0.85); display: flex; align-items: center; gap: 6px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .stat-value { font-size: 1.05rem; font-weight: 800; margin-top: 2px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .stat-sub { font-size: 0.72rem; opacity: 0.9; margin-top: 2px; white-space: nowrap; }
        @media (max-width: 575.98px) {
          .stat-card { padding: 8px 6px; border-radius: 10px; }
          .stat-label { font-size: 0.62rem; gap: 4px; }
          .stat-value { font-size: 0.82rem; }
        }
        
        .manual-trade-card { background: #f8f9fa; border-left: 4px solid #0d6efd; }
        .manual-trade-set { background: #f0f9ff; border-left: 4px solid #0ab64f; }
        .side-up { color: #0ab64f; }
        .side-down { color: #dc3545; }
        .manual-trade-badge { font-size: 0.8rem; padding: 4px 10px; }
        .lt-action-countdown {
          font-size: 1.05rem;
          letter-spacing: 0.02em;
          white-space: nowrap;
        }
        .lt-trades-card {
          overflow: visible !important;
          margin-bottom: 1.25rem !important;
        }
        .lt-mobile-list {
          padding-bottom: 0.25rem;
        }
        .lt-mobile-item {
          padding: 0.75rem 0.9rem;
          border-bottom: 1px solid var(--tb-line, rgba(0,0,0,0.08));
        }
        .lt-mobile-item:last-child {
          border-bottom: none;
          padding-bottom: 1rem;
        }
        .lt-table-wrap {
          max-width: 100%;
          overflow-x: auto;
          -webkit-overflow-scrolling: touch;
        }
        @media (max-width: 767.98px) {
          .lt-trades-card {
            margin-bottom: 2rem !important;
          }
        }
      `}</style>

            {/* Header */}
            <div className="d-flex align-items-center gap-3 mb-3">
                {entity?.image ? (
                    <img className="coin-img" src={entity.image} alt={coinName} />
                ) : (
                    <div
                        className="coin-img d-flex align-items-center justify-content-center"
                        style={{ background: "rgba(13,110,253,0.12)", color: "#0d6efd" }}
                    >
                        <FaListUl />
                    </div>
                )}

<div className="text-muted">
    <span className="fw-semibold text-dark">{coinName}</span> •{" "}
    <span className="fw-semibold">{activePeriod}</span>

    {entity?.period_id ? (
        <>
            {" "}
            • <span className="fw-semibold mono">{safeText(entity?.period_id)}</span>
        </>
    ) : null}

    {" "}
    •{" "}
    <CountdownTimer countdown={countdown} />
</div>
            </div>

            {/* Period Tabs */}
            <div className="card border-0 shadow-sm mb-3">
                <div className="card-body">
                    <div className="tab-grid">
                        {PERIODS.map((p) => {
                            const meta = periodMeta(p);
                            return (
                                <button
                                    type="button"
                                    key={p}
                                    className={`period-tab ${activePeriod === p ? "active" : ""}`}
                                    onClick={() => onTab(p)}
                                >
                                    <div className="period-title">{meta.title}</div>
                                    <div className="period-sub">{meta.sub}</div>
                                </button>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* TradingView chart (below period tabs) */}
            <div className="card border-0 shadow-sm mb-3" style={{ overflow: "hidden", isolation: "isolate", zIndex: 0 }}>
                <div className="card-body py-3">
                    <TradingViewAdvancedChart
                        symbol={tvSymbol}
                        interval={tvInterval}
                        theme="light"
                        height={360}
                    />
                </div>
            </div>

            {/* Manual Trade Result Section */}
            <div className="card mb-3 border-0 shadow-sm">
                <div className={`card-body ${manualTrade.has_manual_trade ? 'manual-trade-set' : 'manual-trade-card'}`}>
                    <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
                        <div className="d-flex align-items-center gap-3">
                            <div>
                                <h5 className="fw-bold mb-1">Manual Trade Result</h5>
                                <div className="text-muted small">
                                    Set the final result for this trading period
                                </div>
                            </div>

                            {manualTrade.has_manual_trade ? (
                                <div className="d-flex align-items-center gap-2">
                                    <span className={`badge manual-trade-badge ${manualTrade.side === 'up' ? 'text-bg-success' : 'text-bg-danger'}`}>
                                        {manualTrade.side === 'up' ? (
                                            <>
                                                <FaCheckCircle className="me-1" /> Up
                                            </>
                                        ) : (
                                            <>
                                                <FaTimesCircle className="me-1" /> Down
                                            </>
                                        )}
                                    </span>
                                    <span className="text-muted small">
                                        Result is set
                                    </span>
                                </div>
                            ) : (
                                <span className="badge manual-trade-badge text-bg-warning">
                                    <FaEdit className="me-1" /> Not Set
                                </span>
                            )}
                        </div>

                        <div className="d-flex gap-2 align-items-center">
                            {!showManualTradeForm ? (
                                <>
                                    <button
                                        type="button"
                                        className="btn btn-primary btn-sm d-inline-flex align-items-center gap-2"
                                        onClick={handleOpenForm}
                                    >
                                        <FaEdit /> {manualTrade.has_manual_trade ? "Change Result" : "Set Result"}
                                    </button>
                                    <CountdownTimer
                                        countdown={countdown}
                                        className="lt-action-countdown d-md-none"
                                    />
                                </>
                            ) : (
                                <button
                                    type="button"
                                    className="btn btn-outline-secondary btn-sm"
                                    onClick={handleCancelForm}
                                >
                                    Cancel
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Manual Trade Form */}
                    {showManualTradeForm && (
                        <div className="mt-3 pt-3 border-top">
                            <style>{`
      .side-radio-wrap{
        display:flex;
        gap:12px;
        flex-wrap:wrap;
      }
      .side-radio-card{
        flex:1;
        min-width:140px;
        border:1px solid rgba(0,0,0,0.14);
        border-radius:14px;
        padding:12px 14px;
        cursor:pointer;
        user-select:none;
        display:flex;
        align-items:center;
        gap:10px;
        transition:transform .08s ease, border-color .08s ease, box-shadow .08s ease, background .08s ease;
        background:#fff;
      }
      .side-radio-card:hover{
        transform:translateY(-1px);
        border-color:rgba(13,110,253,0.35);
        box-shadow:0 6px 14px rgba(0,0,0,0.06);
      }
      .side-radio-card.active{
        border-color:rgba(13,110,253,0.75);
        box-shadow:0 8px 18px rgba(13,110,253,0.12);
        background:rgba(13,110,253,0.05);
      }
      .side-radio-dot{
        width:22px;
        height:22px;
        border-radius:999px;
        border:2px solid rgba(0,0,0,0.25);
        display:flex;
        align-items:center;
        justify-content:center;
        flex:0 0 22px;
      }
      .side-radio-card.active .side-radio-dot{
        border-color:#0d6efd;
      }
      .side-radio-dot::after{
        content:"";
        width:12px;
        height:12px;
        border-radius:999px;
        background:transparent;
        transition:background .08s ease;
      }
      .side-radio-card.active .side-radio-dot::after{
        background:#0d6efd;
      }
      .side-radio-title{
        font-weight:800;
        display:flex;
        align-items:center;
        gap:8px;
      }
      .side-radio-sub{
        font-size:.78rem;
        color:rgba(0,0,0,0.55);
        margin-top:2px;
      }
      .side-radio-up .side-radio-title{ color:#0ab64f; }
      .side-radio-down .side-radio-title{ color:#dc3545; }

      .side-radio-card input{
        position:absolute;
        opacity:0;
        pointer-events:none;
      }
    `}</style>

                            <div className="row g-3 align-items-end">
                                <div className="col-12 col-md-6 col-lg-5">
                                    <label className="form-label fw-semibold mb-2">Select Side</label>

                                    <div className="side-radio-wrap">
                                        <label
                                            className={`side-radio-card side-radio-up ${manualTradeSide === "up" ? "active" : ""}`}
                                            onClick={() => setManualTradeSide("up")}
                                        >
                                            <input
                                                type="radio"
                                                name="manualTradeSide"
                                                value="up"
                                                checked={manualTradeSide === "up"}
                                                onChange={() => setManualTradeSide("up")}
                                            />
                                            <div className="side-radio-dot" />
                                            <div>
                                                <div className="side-radio-title">
                                                    <FaArrowUp /> Up
                                                </div>
                                                <div className="side-radio-sub">Winners: Up side</div>
                                            </div>
                                        </label>

                                        <label
                                            className={`side-radio-card side-radio-down ${manualTradeSide === "down" ? "active" : ""}`}
                                            onClick={() => setManualTradeSide("down")}
                                        >
                                            <input
                                                type="radio"
                                                name="manualTradeSide"
                                                value="down"
                                                checked={manualTradeSide === "down"}
                                                onChange={() => setManualTradeSide("down")}
                                            />
                                            <div className="side-radio-dot" />
                                            <div>
                                                <div className="side-radio-title">
                                                    <FaArrowDown /> Down
                                                </div>
                                                <div className="side-radio-sub">Winners: Down side</div>
                                            </div>
                                        </label>
                                    </div>
                                </div>

                                <div className="col-12 col-md-6 col-lg-4">
                                    <div className="text-muted small">
                                        Current: {entity_id} • {activePeriod}
                                    </div>
                                </div>

                                <div className="col-12 col-lg-3 d-flex justify-content-lg-end align-items-center gap-2">
                                    <button
                                        type="button"
                                        className="btn btn-success d-inline-flex align-items-center gap-2"
                                        onClick={setManualTradeResult}
                                        disabled={!manualTradeSide || isSettingManualTrade}
                                    >
                                        <FaSave className={isSettingManualTrade ? "tb-spin" : ""} />
                                        {isSettingManualTrade ? "Setting..." : "Save Result"}
                                    </button>
                                    <CountdownTimer
                                        countdown={countdown}
                                        className="lt-action-countdown d-md-none"
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                </div>
            </div>

            {/* Content - Show loading only on initial load */}
            {isLoading ? (
                <div className="card border-0 shadow-sm">
                    <div className="card-body text-muted">Loading live trades…</div>
                </div>
            ) : (
                <>
                    {/* Stats with colored cards and icons — 3 columns on all screen sizes */}
                    <div className="row g-2 mb-3 stat-cards-row">
                        <div className="col-4">
                            <div className="stat-card stat-total">
                                <div className="stat-label">
                                    <FaChartLine /> Total Coin
                                </div>
                                <div className="stat-value">{nfmt(stats.total, 0)}</div>
                                <div className="stat-sub">{nfmt(stats.totalCount)} trades</div>
                            </div>
                        </div>
                        <div className="col-4">
                            <div className="stat-card stat-up">
                                <div className="stat-label">
                                    <FaArrowTrendUp /> Up Coin
                                </div>
                                <div className="stat-value">{nfmt(stats.up, 0)}</div>
                                <div className="stat-sub">{nfmt(stats.upCount)} trades</div>
                            </div>
                        </div>
                        <div className="col-4">
                            <div className="stat-card stat-down">
                                <div className="stat-label">
                                    <FaArrowTrendDown /> Down Coin
                                </div>
                                <div className="stat-value">{nfmt(stats.down, 0)}</div>
                                <div className="stat-sub">{nfmt(stats.downCount)} trades</div>
                            </div>
                        </div>
                    </div>

                    {/* Table / mobile list */}
                    <div className="card border-0 shadow-sm lt-trades-card mb-3">
                        <div className="card-body p-0">
                            {/* Mobile: stacked records (avoids clipped end columns/rows) */}
                            <div className="d-md-none lt-mobile-list">
                                {tradeList.length === 0 ? (
                                    <div className="text-center text-muted py-5 px-3">
                                        <div className="fw-semibold">No live trades</div>
                                        <div className="small">Data updates automatically.</div>
                                    </div>
                                ) : (
                                    tradeList.map((row, idx) => {
                                        const u = row?.user || {};
                                        const t = row?.trade || {};
                                        const s = sideNorm(t?.side);
                                        const mobileDisplay =
                                            safeText(u?.mobile) === "—"
                                                ? "—"
                                                : `+${safeText(u?.country_code) === "—" ? "" : safeText(u?.country_code)} ${safeText(
                                                    u?.mobile
                                                )}`.trim();

                                        return (
                                            <div
                                                key={`m-${safeText(u?.user_id)}-${safeText(t?.amount)}-${idx}`}
                                                className="lt-mobile-item"
                                            >
                                                <div className="d-flex align-items-center justify-content-between gap-2 mb-1">
                                                    {s === "up" ? (
                                                        <span className="badge text-bg-success d-inline-flex align-items-center gap-1">
                                                            <FaArrowUp /> Up
                                                        </span>
                                                    ) : s === "down" ? (
                                                        <span className="badge text-bg-danger d-inline-flex align-items-center gap-1">
                                                            <FaArrowDown /> Down
                                                        </span>
                                                    ) : (
                                                        <span className="badge text-bg-secondary">—</span>
                                                    )}
                                                    <span className="fw-semibold">{moneyFmt(t?.amount, 2)}</span>
                                                </div>
                                                <div className="fw-semibold small">{safeText(u?.name)}</div>
                                                <div className="text-muted small">
                                                    ID {safeText(u?.user_id)} · {mobileDisplay}
                                                </div>
                                                <div className="text-muted small text-break">{safeText(u?.email)}</div>
                                            </div>
                                        );
                                    })
                                )}
                            </div>

                            {/* Desktop/tablet table */}
                            <div className="d-none d-md-block p-2 table-responsive lt-table-wrap">
                                <table className="table table-sm align-middle mb-0 lt-table">
                                    <thead className="table-light">
                                        <tr>
                                            <th style={{ width: 90 }}>Side</th>
                                            <th style={{ width: 100 }} className="text-end">
                                                Amount
                                            </th>
                                            <th style={{ width: 140 }}>User ID</th>
                                            <th style={{ width: 220 }}>Name</th>
                                            <th style={{ width: 220 }}>Mobile</th>
                                            <th style={{ width: 260 }}>Email</th>
                                        </tr>
                                    </thead>

                                    <tbody>
                                        {tradeList.length === 0 ? (
                                            <tr>
                                                <td colSpan={6} className="text-center text-muted py-5">
                                                    <div className="fw-semibold">No live trades</div>
                                                    <div className="small">Data updates automatically.</div>
                                                </td>
                                            </tr>
                                        ) : (
                                            tradeList.map((row, idx) => {
                                                const u = row?.user || {};
                                                const t = row?.trade || {};
                                                const s = sideNorm(t?.side);

                                                const mobileDisplay =
                                                    safeText(u?.mobile) === "—"
                                                        ? "—"
                                                        : `+${safeText(u?.country_code) === "—" ? "" : safeText(u?.country_code)} ${safeText(
                                                            u?.mobile
                                                        )}`.trim();

                                                return (
                                                    <tr key={`${safeText(u?.user_id)}-${safeText(t?.amount)}-${idx}`}>
                                                        <td>
                                                            {s === "up" ? (
                                                                <span className="badge text-bg-success d-inline-flex align-items-center gap-1">
                                                                    <FaArrowUp /> Up
                                                                </span>
                                                            ) : s === "down" ? (
                                                                <span className="badge text-bg-danger d-inline-flex align-items-center gap-1">
                                                                    <FaArrowDown /> Down
                                                                </span>
                                                            ) : (
                                                                <span className="badge text-bg-secondary">—</span>
                                                            )}
                                                        </td>

                                                        <td className="text-end fw-semibold">{moneyFmt(t?.amount, 2)}</td>

                                                        <td className="mono fw-semibold">{safeText(u?.user_id)}</td>

                                                        <td className="fw-semibold">{safeText(u?.name)}</td>

                                                        <td className="fw-semibold">{mobileDisplay}</td>

                                                        <td className="fw-semibold" style={{ wordBreak: "break-word" }}>
                                                            {safeText(u?.email)}
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </PageTransition>
    );
}